/**
 * DocuSign Envelopes workbench — reached from the top-right button on Credit
 * Approved Deals (UK & AUS via the region switch). Everything staff used to
 * open the DocuSign dropdown for lives here: resend / correct / void, resume or
 * discard drafts, history & audit trail, signed PDF + certificate, DocuSign's
 * own correct view embedded, reminders & expiry.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRegion } from "@/contexts/RegionContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ArrowLeft, FileSignature, RefreshCw, Loader2, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { listDocuSignEnvelopes, getEnvelopeCorrectView, getEnvelopeResumeView } from "@/lib/api";
import { EnvelopeRow } from "./EnvelopeRow";
import { EnvelopeHistoryDrawer } from "./EnvelopeHistoryDrawer";
import { EnvelopeNotificationDialog } from "./EnvelopeNotificationDialog";
import { EnvelopeIframeDialog } from "./EnvelopeIframeDialog";
import { isOpen, isInFlight, daysAgo } from "./envelopeUtils";

const SF_BLUE = "#2E71F0";

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="px-3 py-2 rounded-md border bg-white text-xs whitespace-nowrap" data-testid={testId}>
    <span className={`font-bold text-base ${tone}`}>{value}</span>
    <span className="text-muted-foreground"> {label}</span>
  </div>
);

export default function DocuSignEnvelopesPage() {
  const navigate = useNavigate();
  const { currentRegion } = useRegion();
  const isAus = currentRegion === "AUS";
  const locale = isAus ? "en-AU" : "en-GB";

  const [envelopes, setEnvelopes] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState("30");
  const [filter, setFilter] = useState("open");
  const [query, setQuery] = useState("");

  const [history, setHistory] = useState(null);
  const [notif, setNotif] = useState(null);
  const [frame, setFrame] = useState(null);     // {env, url, title, hint}
  const [opening, setOpening] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const d = await listDocuSignEnvelopes(isAus ? "aus" : "uk", parseInt(days, 10), silent);
      setEnvelopes(d.envelopes || []);
      setTruncated(!!d.truncated);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load DocuSign envelopes");
    } finally {
      setLoading(false);
    }
  }, [isAus, days]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return envelopes.filter((e) => {
      if (filter === "open" && !isOpen(e)) return false;
      if (filter === "completed" && e.status !== "completed") return false;
      if (filter === "attention" && !(isInFlight(e) && (daysAgo(e.sent_at) ?? 0) >= 7) && e.status !== "declined") return false;
      if (!q) return true;
      const hay = [e.subject, e.company_name, e.application_id, e.envelope_id, ...(e.signers || []).flatMap((s) => [s.name, s.email])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [envelopes, filter, query]);

  const stats = useMemo(() => ({
    open: envelopes.filter(isOpen).length,
    waiting: envelopes.filter((e) => isInFlight(e) && (daysAgo(e.sent_at) ?? 0) >= 7).length,
    drafts: envelopes.filter((e) => e.status === "created").length,
    completed: envelopes.filter((e) => e.status === "completed").length,
    declined: envelopes.filter((e) => e.status === "declined" || e.status === "voided").length,
  }), [envelopes]);

  const openCorrect = async (env) => {
    setOpening(env.envelope_id);
    try {
      const d = await getEnvelopeCorrectView(env.envelope_id);
      setFrame({ env, url: d.url, title: "DocuSign — Correct envelope", hint: "Swap documents, move or add fields, change recipients — then click Correct / Send inside DocuSign" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the correct view"); }
    finally { setOpening(null); }
  };

  const openResume = async (env) => {
    setOpening(env.envelope_id);
    try {
      const d = await getEnvelopeResumeView(env.envelope_id);
      if (!d.sender_view_url) { toast.info(d.message || "This draft cannot be resumed"); return; }
      setFrame({ env, url: d.sender_view_url, title: "DocuSign — Place signature fields", hint: "Finish tagging and click Send in DocuSign" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resume the draft"); }
    finally { setOpening(null); }
  };

  const closeFrame = (refresh) => { setFrame(null); if (refresh) load(true); };
  const closeNotif = useCallback(() => setNotif(null), []);

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="docusign-envelopes-page">
        <PageHeader
          title="DocuSign Envelopes"
          description={`Every envelope sent from the portal${isAus ? " — Australia" : " — UK"}: track, chase, correct, download`}
          breadcrumb={
            <Button variant="ghost" size="sm" className="-ml-2 h-7 text-xs" onClick={() => navigate("/credit-deals")} data-testid="back-to-credit-deals">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Credit Approved Deals
            </Button>
          }
        >
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="env-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => load(true)} disabled={loading} style={{ backgroundColor: SF_BLUE }} className="hover:opacity-90" data-testid="env-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Refresh
            </Button>
          </div>
        </PageHeader>

        <div className="flex flex-wrap items-center gap-2 -mt-4 mb-5" data-testid="env-stats">
          <Stat label="open" value={stats.open} tone="text-sky-700" testId="env-stat-open" />
          <Stat label="waiting 7d+" value={stats.waiting} tone="text-amber-600" testId="env-stat-waiting" />
          <Stat label="drafts" value={stats.drafts} tone="text-slate-600" testId="env-stat-drafts" />
          <Stat label="completed" value={stats.completed} tone="text-emerald-600" testId="env-stat-completed" />
          <Stat label="declined / voided" value={stats.declined} tone="text-rose-600" testId="env-stat-declined" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search company, SF#, signer or envelope id…" className="pl-8 h-9" data-testid="env-search" />
          </div>
          <div className="flex items-center gap-1 rounded-md border bg-white p-0.5" data-testid="env-filter">
            {[["open", "Open"], ["attention", "Needs attention"], ["completed", "Completed"], ["all", "All"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={`px-2.5 h-7 rounded text-xs font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`env-filter-${k}`}>{l}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: SF_BLUE }} /> Loading envelopes from DocuSign…</div>
        ) : error ? (
          <div className="text-center py-12" data-testid="env-error">
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <p className="text-sm text-rose-700">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => load()}>Try again</Button>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="env-empty">
            <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>{filter === "open" ? "No open envelopes" : filter === "attention" ? "Nothing needs chasing" : filter === "completed" ? "No completed envelopes in this window" : "No envelopes in this window"}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="env-list">
            {visible.map((env) => (
              <EnvelopeRow key={env.envelope_id} env={env} locale={locale} onChanged={() => load(true)}
                onHistory={setHistory} onNotification={setNotif} onCorrectView={openCorrect} onResume={openResume} />
            ))}
            <p className="text-[11px] text-muted-foreground pt-1" data-testid="env-count">{visible.length} of {envelopes.length} envelopes in the last {days} days{truncated ? " · showing the 1,000 most recently updated across UK + AUS — narrow the window to see everything" : ""}</p>
          </div>
        )}

        {opening && <div className="fixed bottom-4 right-4 rounded-md bg-slate-900 text-white text-xs px-3 py-2 flex items-center gap-2 shadow-lg"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening DocuSign…</div>}

        <EnvelopeHistoryDrawer envelope={history} open={!!history} onClose={() => setHistory(null)} locale={locale} />
        <EnvelopeNotificationDialog envelope={notif} open={!!notif} onClose={closeNotif} />
        <EnvelopeIframeDialog open={!!frame} url={frame?.url} title={frame?.title} hint={frame?.hint} envelope={frame?.env}
          onClose={() => closeFrame(false)} onDone={() => { toast.success("Refreshing envelope status…"); closeFrame(true); }} />
      </div>
    </TooltipProvider>
  );
}
